import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import type { UseQueryOptionsWithoutQueryKey } from '../../react-query-config'
import type { ResponseApiError } from '../../models/axios-error'
import type { AggregationLevel, Channel } from './home.model'

const QUERY_KEY_WEEKLY_GROSS_MERCHANDISE_VALUE_PROGRESS = 'get-weekly-gross-merchandise-value-progress'
const WEEKLY_GMV_PROGRESS_URL = '/v2/weekly-gross-merchandise-value-progress'

export interface UseApiGetWeeklyGmvProgressRequest {
  channel: Channel
  aggregation: AggregationLevel
  storeId: number
  teamId: number | null
  week: string
}

interface WeeklyGmvProgressResponse {
  realized_gmv?: number
  forecast_gmv?: number
  target_percent?: number
  realized_in_error?: boolean
  forecast_in_error?: boolean
}

export interface WeeklyGmvProgress {
  realizedGmv: number | null
  forecastGmv: number | null
  targetPercent: number
  realizedInError: boolean
  forecastInError: boolean
}

export const toWeeklyGmvProgress = (response: WeeklyGmvProgressResponse): WeeklyGmvProgress => ({
  realizedGmv: response.realized_gmv ?? null,
  forecastGmv: response.forecast_gmv ?? null,
  targetPercent: response.target_percent ?? 0,
  realizedInError: response.realized_in_error ?? false,
  forecastInError: response.forecast_in_error ?? false
})

export const buildWeeklyGmvProgressParams = ({
  aggregation,
  channel,
  storeId,
  teamId,
  week
}: UseApiGetWeeklyGmvProgressRequest) => ({
  aggregation,
  channel,
  storeId,
  ...(aggregation === 'TEAM' && teamId !== null ? { teamId } : {}),
  week
})

const getWeeklyGmvProgress = async (params: UseApiGetWeeklyGmvProgressRequest): Promise<WeeklyGmvProgress> => {
  const response = await axios<WeeklyGmvProgressResponse>({
    method: 'get',
    url: WEEKLY_GMV_PROGRESS_URL,
    params: buildWeeklyGmvProgressParams(params)
  })

  return toWeeklyGmvProgress(response.data)
}

export const useApiGetWeeklyGmvProgress = (
  params: UseApiGetWeeklyGmvProgressRequest,
  enabled: boolean,
  options?: UseQueryOptionsWithoutQueryKey<WeeklyGmvProgress>
) =>
  useQuery<WeeklyGmvProgress, ResponseApiError>({
    queryKey: [QUERY_KEY_WEEKLY_GROSS_MERCHANDISE_VALUE_PROGRESS, params],
    queryFn: () => getWeeklyGmvProgress(params),
    enabled,
    ...options
  })
